import { useState, useEffect } from "react"

import axiosInstance, { Endpoint } from "api"
import { AxiosResponse } from "axios"
import { useQuery } from "react-query"
import { IClassShort } from "ts/interfaces/Class"
import { IStudentDetailed } from "ts/interfaces/Student"

export interface ICheckClassesWithStudentsArgs {
  classesData: IClassShort[] | undefined
  isLoadingClasses: boolean
}

export interface ICheckClassesWithStudentsReturn {
  isLoadingStudents: boolean
  hasClassWithStudents: boolean
  foundClassWithStudents: number | null
}

export const useCheckClassesWithStudents = ({
  classesData,
  isLoadingClasses,
}: ICheckClassesWithStudentsArgs): ICheckClassesWithStudentsReturn => {
  const [foundClassWithStudents, setFoundClassWithStudents] = useState<
    number | null
  >(null)
  const [currentClassIndex, setCurrentClassIndex] = useState(0)

  useEffect(() => {
    if (classesData) {
      setCurrentClassIndex(0)
      setFoundClassWithStudents(null)
    }
  }, [classesData])

  const currentClass = classesData?.[currentClassIndex]

  const queryEnabled = !!classesData?.length &&
    !isLoadingClasses &&
    !foundClassWithStudents &&
    currentClassIndex < (classesData?.length || 0)

  const { isLoading: isLoadingStudents } = useQuery(
    ["classesStudents", currentClass?.id, "check"],
    () =>
      axiosInstance(
        Endpoint.CLASSES_ID_STUDENTS.replace("ID", `${currentClass?.id}`),
        {
          params: {
            skip: 0,
            limit: 1,
          },
        }
      ),
    {
      enabled: queryEnabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      onSuccess: (data: AxiosResponse<IStudentDetailed>) => {
        const responseData = data?.data as IStudentDetailed | undefined
        if (
          responseData?.all_objects_count &&
          responseData.all_objects_count > 0
        ) {
          setFoundClassWithStudents(currentClassIndex)
        } else {
          setCurrentClassIndex((prev) => prev + 1)
        }
      },
      onError: () => {
        setCurrentClassIndex((prev) => prev + 1)
      },
    }
  )

  const hasClassWithStudents = foundClassWithStudents !== null

  return {
    isLoadingStudents,
    hasClassWithStudents,
    foundClassWithStudents,
  }
}
